import React from "react";
import { Twitter, Facebook, Linkedin, Mail, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * Share strip for blog posts. Deliberately explicit (Twitter / Facebook /
 * LinkedIn / email / copy) rather than the ambiguous native-share sheet,
 * because readers on desktop don't have `navigator.share`.
 */
export default function ShareStrip({ url, title, description }) {
  const enc = (s) => encodeURIComponent(s || "");
  const share = {
    twitter: `https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`,
    email: `mailto:?subject=${enc(title)}&body=${enc((description || "") + "\n\n" + url)}`,
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — long-press the URL bar instead");
    }
  };

  const btn = "inline-flex items-center gap-2 border border-obsidian-600 hover:border-terracotta hover:text-terracotta text-bone-400 font-sans uppercase tracking-[0.24em] text-[0.6rem] px-3 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900";

  return (
    <div
      className="mt-10 pt-6 border-t border-obsidian-600 flex items-center gap-2 flex-wrap"
      data-testid="share-strip"
      aria-label="Share this essay"
    >
      <span className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-bone-500 mr-1">
        Share
      </span>
      <a
        href={share.twitter}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-twitter"
        aria-label="Share on X / Twitter"
        className={btn}
      >
        <Twitter className="h-3 w-3" aria-hidden="true" />
        X
      </a>
      <a
        href={share.facebook}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-facebook"
        aria-label="Share on Facebook"
        className={btn}
      >
        <Facebook className="h-3 w-3" aria-hidden="true" />
        Facebook
      </a>
      <a
        href={share.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="share-linkedin"
        aria-label="Share on LinkedIn"
        className={btn}
      >
        <Linkedin className="h-3 w-3" aria-hidden="true" />
        LinkedIn
      </a>
      <a
        href={share.email}
        data-testid="share-email"
        aria-label="Share via email"
        className={btn}
      >
        <Mail className="h-3 w-3" aria-hidden="true" />
        Email
      </a>
      <button onClick={copy} data-testid="share-copy" className={btn}>
        <LinkIcon className="h-3 w-3" aria-hidden="true" />
        Copy
      </button>
    </div>
  );
}
